import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { searchItems, refetchSingleItem } from '../firebase/course';
import { useMetadata } from '../contexts/MetadataProvider';
import { useAuth } from '../firebase/useAuth';
import { useModal } from '../context/ModalContext';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import DetailsModal from '../components/modals/DetailsModal/DetailsModal';
import SearchSidebarControls from '../components/search/SearchSidebarControls';
import MobileSidebar from '../components/course/MobileSidebar';
import SearchInput from '../components/search/SearchInput';
import CourseContentGridSkeleton from '../components/course/skeletons/CourseContentGridSkeleton';
import ItemGrid from '../components/common/ItemGrid';
import { FunnelIcon, ChevronDownIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

const SEARCH_ITEM_LIMIT = 100;

function SearchPage() {
  const { userId } = useAuth();
  const { learnedWordIds, learnedSentenceIds, wordIdsLoading, sentenceIdsLoading } = useMetadata();
  const learnedWordIdsRef = useRef(learnedWordIds);
  const learnedSentenceIdsRef = useRef(learnedSentenceIds);
  useEffect(() => { learnedWordIdsRef.current = learnedWordIds; }, [learnedWordIds]);
  useEffect(() => { learnedSentenceIdsRef.current = learnedSentenceIds; }, [learnedSentenceIds]);
  const location = useLocation();
  const { showModal, hideModal } = useModal();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('words');
  const [wordSearchBy, setWordSearchBy] = useState('kana');
  const [filters, setFilters] = useState({ level: 'all', learned: 'all', srsStage: 'all' });
  const [allItems, setAllItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const mobileFiltersRef = useRef(null);
  const [isMobileFiltersVisible, setIsMobileFiltersVisible] = useState(false);
  const [showStickyFilterButton, setShowStickyFilterButton] = useState(false);
  const isNavigatingFromModal = useRef(false);


  const performSearch = useCallback(
    async (query, type = searchType, searchBy = wordSearchBy) => {
      if (!query.trim()) {
        setAllItems([]);
        return;
      }
      setIsLoading(true);
      setHasSearched(true);
      try {
        const result = await searchItems(query, type, searchBy, SEARCH_ITEM_LIMIT, userId, type === 'words' ? learnedWordIdsRef.current : learnedSentenceIdsRef.current);
        setAllItems(result.results);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setAllItems([]);
      } finally {
        setIsLoading(false);
      }
    },
    [searchType, wordSearchBy, userId]
  );

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    performSearch(searchQuery);
  };

  // Handle item updates from DetailsModal actions
  const handleItemUpdate = useCallback(async (itemId) => {
    try {
      // Refetch the updated item data from server
      const updatedItem = await refetchSingleItem(itemId, userId, searchType);
      
      setAllItems(prevItems => {
        const itemIndex = prevItems.findIndex(item => item.id === itemId);
        if (itemIndex === -1) return prevItems;
        
        const newItems = [...prevItems];
        // Replace with the updated item data
        newItems[itemIndex] = updatedItem;
        return newItems;
      });
    } catch (error) {
      console.error('Failed to refetch item:', error);
    }
  }, [userId, searchType]);

  const handleOpenModal = useCallback(
    (item) => showModal(<DetailsModal item={item} handleClose={hideModal} onUpdate={handleItemUpdate} />),
    [showModal, hideModal, handleItemUpdate]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyFilterButton(!entry.isIntersecting);
      },
      { rootMargin: '-80px 0px 0px 0px' }
    );
    const currentRef = mobileFiltersRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  useEffect(() => {
    const { initialQuery, initialType, initialSearchBy } = location.state || {};
    if (initialQuery !== undefined) {
      const newType = initialType || 'words';
      const newSearchBy = initialSearchBy || 'kana';
      const idsLoading = newType === 'words' ? wordIdsLoading : sentenceIdsLoading;
      if (idsLoading) return;

      isNavigatingFromModal.current = true;
      setSearchQuery(initialQuery);
      setSearchType(newType);
      setWordSearchBy(newSearchBy);
      performSearch(initialQuery, newType, newSearchBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, wordIdsLoading, sentenceIdsLoading]);

  useEffect(() => {
    // Skip this effect if we're navigating from modal
    if (isNavigatingFromModal.current) {
      isNavigatingFromModal.current = false;
      return;
    }
    
    // Clear results and re-search when search type changes
    setAllItems([]);
    setHasSearched(false);
    
    if (searchQuery.trim()) {
      performSearch(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchType]);

  const displayedItems = useMemo(() => {
    const activeLearnedIds = searchType === 'words' ? learnedWordIds : learnedSentenceIds;
    return allItems.filter((item) => {
      const levelMatch = filters.level === 'all' || String(item.level) === filters.level;
      const learnedMatch = filters.learned === 'all' || (filters.learned === 'learned' ? activeLearnedIds.has(item.id) : !activeLearnedIds.has(item.id));
      const srsStageMatch = filters.srsStage === 'all' || (item.srs_stage != null && String(item.srs_stage) === filters.srsStage);
      return levelMatch && learnedMatch && srsStageMatch;
    });
  }, [allItems, filters, searchType, learnedWordIds, learnedSentenceIds]);

  return (
    <SkeletonTheme baseColor="var(--color-skeleton-base)" highlightColor="var(--color-skeleton-highlight)">
      <div className="flex flex-col min-h-screen bg-background-primary">
        <MainNavbar />
        <main className="flex-grow">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <SearchInput
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchType={searchType}
              setSearchType={setSearchType}
              wordSearchBy={wordSearchBy}
              setWordSearchBy={setWordSearchBy}
              onSubmit={handleSearchSubmit}
              wrapperClassName="max-w-3xl mx-auto mb-8"
            />
            <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-6 gap-8 mt-8">
              <aside className="hidden lg:block lg:col-span-1 xl:col-span-1 sticky top-24 self-start">
                <SearchSidebarControls filters={filters} setFilters={setFilters} />
              </aside>
              <div className="lg:col-span-4 xl:col-span-5">
                <div ref={mobileFiltersRef} className="lg:hidden mb-6">
                  <button
                    onClick={() => setIsMobileFiltersVisible(!isMobileFiltersVisible)}
                    className="flex w-full items-center justify-between rounded-lg bg-background-secondary p-3 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <FunnelIcon className="h-5 w-5 text-content-secondary" />
                      <span className="font-semibold text-content-primary">Filters</span>
                    </div>
                    <ChevronDownIcon
                      className={`h-5 w-5 text-content-secondary transition-transform duration-200 ${isMobileFiltersVisible ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isMobileFiltersVisible && (
                    <div className="mt-4 rounded-lg bg-background-secondary p-4">
                      <SearchSidebarControls filters={filters} setFilters={setFilters} />
                    </div>
                  )}
                </div>
                {isLoading ? (
                  <>
                    <div className="mb-4">
                      <Skeleton width={120} height={20} />
                    </div>
                    <CourseContentGridSkeleton contentType={searchType} />
                  </>
                ) : hasSearched ? (
                  displayedItems.length > 0 ? (
                    <>
                      <p className="mb-4 text-content-secondary">
                        {displayedItems.length} {displayedItems.length === 1 ? 'result' : 'results'} found.
                      </p>
                      <ItemGrid items={displayedItems} contentType={searchType} onCardClick={handleOpenModal} learnedIds={searchType === 'words' ? learnedWordIds : learnedSentenceIds} />
                    </>
                  ) : (
                    // STEP 2: Update the JSX for the "No Results Found" message
                    <div className="text-center py-16 flex flex-col items-center">
                      <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6 border border-border">
                        <ExclamationTriangleIcon className="w-10 h-10 text-content-secondary" />
                      </div>
                      <h3 className="text-xl font-semibold text-content-primary">No Results Found</h3>
                      <p className="text-content-secondary mt-2">Try adjusting your search or filters.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-16">
                    <h3 className="text-xl font-semibold">Search for words and sentences</h3>
                    <p className="text-content-secondary mt-2">Enter a query above to begin.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <MainFooter />
        <>
          {showStickyFilterButton && (
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden fixed top-24 left-4 z-30 flex items-center justify-center h-12 w-12 bg-background-primary rounded-full shadow-lg border border-border"
              aria-label="Open Filters"
            >
              <FunnelIcon className="h-6 w-6 text-content-primary" />
            </button>
          )}
          <MobileSidebar isOpen={isMobileSidebarOpen} onClose={() => setIsMobileSidebarOpen(false)}>
            <SearchSidebarControls filters={filters} setFilters={setFilters} />
          </MobileSidebar>
        </>
      </div>
    </SkeletonTheme>
  );
}

export default SearchPage;